import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, BarChart3, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Herramientas", href: "#herramientas", testid: "nav-link-herramientas" },
  { label: "Análisis", href: "#analisis", testid: "nav-link-analisis" },
  { label: "Guías", href: "#guias", testid: "nav-link-guias" },
];

export const Logo = ({ dark = false }) => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/25">
      <BarChart3 className="h-5 w-5 text-white" strokeWidth={2.5} />
    </div>
    <span className={`font-display text-xl font-extrabold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>
      Piso<span className="text-emerald-500">Score</span>
    </span>
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = `/${href}`;
    } else {
      scrollToId(href);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    } else {
      window.__lenis?.scrollTo(0, { duration: 1.2 });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 border-b bg-white/80 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? "border-slate-200 shadow-sm shadow-slate-900/5" : "border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="/" onClick={(e) => { e.preventDefault(); goHome(); }} aria-label="PisoScore inicio">
          <Logo />
        </a>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={l.testid}
              href={l.href}
              onClick={(e) => {
                e.preventDefault();
                go(l.href);
              }}
              className="group relative text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-emerald-500 transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-analyze-btn"
            onClick={() => go("#analisis")}
            className="group hidden items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-lg hover:shadow-emerald-500/25 sm:inline-flex"
          >
            Analizar un piso
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="rounded-lg p-2 text-slate-700 md:hidden"
            aria-label="Menú"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-100 bg-white md:hidden"
          >
            <div className="space-y-1 px-4 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  href={l.href}
                  onClick={(e) => {
                    e.preventDefault();
                    go(l.href);
                  }}
                  className="block w-full rounded-xl px-4 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-50"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-analyze-btn-mobile"
                onClick={() => go("#analisis")}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white"
              >
                Analizar un piso <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
